import React, { useState, useContext } from 'react';
import { TextField, Button, Box, Typography, CircularProgress } from '@mui/material';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import { createUser } from '../graphql/mutations';
import { listUsers } from '../graphql/queries';
import { AppContext } from '../App';

// Email validation function
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const CreateUserForm = ({ onUserCreated }) => {
  const { userRecord } = useContext(AppContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      // Validate email format
      if (value && !isValidEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setLoading(true);
    
    // Validate email before submission
    if (!isValidEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    // Set username to email value
    const username = formData.email;
    
    try {
      console.log('Attempting to create user with data:', { ...formData, username });
      
      // Normalize email to lowercase for consistent checking
      const normalizedEmail = formData.email.toLowerCase().trim();
      
      // Check if user with this email already exists in the database (case-insensitive)
      try {
        const existingUsersResponse = await API.graphql(
          graphqlOperation(listUsers, {
            filter: { email: { eq: normalizedEmail } },
          })
        );

        // Case-insensitive email comparison (should already match due to normalization, but double-check)
        const existingUser = existingUsersResponse?.data?.listUsers?.items?.find(
          (user) => user.email && user.email.toLowerCase().trim() === normalizedEmail
        );

        if (existingUser) {
          setEmailError('An account with this email address already exists.');
          setError('An account with this email address already exists. Please use a different email address.');
          setLoading(false);
          return;
        }
      } catch (checkError) {
        console.error('Error checking for existing user:', checkError);
        // Continue with user creation if check fails (don't block on check error)
      }
      
      // Get the current session to ensure we're authenticated
      const session = await Auth.currentSession();
      console.log('Current session:', session);

      // Create user in Cognito using the Lambda function
      const cognitoResponse = await API.post('createUser', '/users/create', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': session.getIdToken().getJwtToken()
        },
        body: {
          email: formData.email,
          username: username,
          password: formData.password
        }
      });

      console.log('Cognito Response:', cognitoResponse);

      if (cognitoResponse.success) {
        // Create user in the database with priceListInvites
        // Normalize email to lowercase to prevent duplicates
        try {
          const normalizedEmail = formData.email.toLowerCase().trim();
          
          // CRITICAL: Check one more time if user already exists before creating
          console.log(`[CreateUserForm] Checking if user ${normalizedEmail} already exists in database...`);
          const existingUsersResponse = await API.graphql(
            graphqlOperation(listUsers, { limit: 10000 })
          );
          const allUsers = existingUsersResponse?.data?.listUsers?.items || [];
          const existingMatches = allUsers.filter(
            (u) => u.email && u.email.toLowerCase().trim() === normalizedEmail
          );
          
          if (existingMatches.length > 0) {
            console.error(`[CreateUserForm] ⚠️⚠️⚠️ USER ALREADY EXISTS IN DATABASE! ⚠️⚠️⚠️`);
            console.error(`[CreateUserForm] Found ${existingMatches.length} user(s) with email ${normalizedEmail}:`);
            existingMatches.forEach((u, idx) => {
              console.error(`  [${idx}] ID: ${u.id}, createdByUserId: ${u.createdByUserId}`);
            });
            console.error(`[CreateUserForm] NOT creating duplicate. Using existing user.`);
            
            setFormData({ email: '', password: '' });
            setEmailError('');
            setLoading(false);
            if (onUserCreated) {
              onUserCreated();
            }
            return;
          }
          
          console.log(`[CreateUserForm] No existing user found. Creating new user...`);
          
          const dbUserInput = {
            email: normalizedEmail,
            username: username.toLowerCase().trim(),
            createdByUserId: userRecord.id,
            priceListInvites: [userRecord.id]
          };
          
          console.log(`[CreateUserForm] Creating user with:`, dbUserInput);
          
          const dbUserResponse = await API.graphql(
            graphqlOperation(createUser, { input: dbUserInput })
          );
          
          const createdUser = dbUserResponse.data.createUser;
          console.log('[CreateUserForm] Database user created:', {
            id: createdUser.id,
            email: createdUser.email,
            createdByUserId: createdUser.createdByUserId,
          });
          
          setFormData({ email: '', password: '' });
          setEmailError('');
          setLoading(false);
          if (onUserCreated) {
            onUserCreated();
          }
        } catch (dbError) {
          console.error('Error creating user in database:', dbError);
          setError('User created in Cognito but failed to create in database. Please try again.');
          setLoading(false);
        }
      } else {
        // Check if the error is about duplicate email/user
        const errorMessage = cognitoResponse.message || 'Failed to create user';
        if (errorMessage.toLowerCase().includes('already exists') || 
            errorMessage.toLowerCase().includes('duplicate') ||
            errorMessage.toLowerCase().includes('email')) {
          setEmailError('An account with this email address already exists.');
          setError('An account with this email address already exists. Please use a different email address.');
        } else {
          setError(errorMessage);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        stack: err.stack,
        name: err.name,
        code: err.code
      });
      
      if (err.response) {
        const errorMessage = err.response.data?.message || err.message;
        console.error('Response data:', err.response.data);
        
        // Check if error is about duplicate email/user
        if (errorMessage.toLowerCase().includes('already exists') || 
            errorMessage.toLowerCase().includes('duplicate') ||
            errorMessage.toLowerCase().includes('email') ||
            err.response.data?.code === 'UsernameExistsException' ||
            err.response.data?.code === 'AliasExistsException') {
          setEmailError('An account with this email address already exists.');
          setError('An account with this email address already exists. Please use a different email address.');
        } else {
          setError(`Server error: ${errorMessage}`);
        }
      } else if (err.request) {
        console.error('Request error:', err.request);
        setError('No response received from server. Please check your connection.');
      } else {
        console.error('General error:', err);
        setError(`Error: ${err.message || 'An unexpected error occurred'}`);
      }
      setLoading(false);
    }
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2
      }}
    >
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <TextField
        fullWidth
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        required
        error={!!emailError}
        helperText={emailError}
      />
      <TextField
        fullWidth
        label="Password"
        name="password"
        type="password"
        value={formData.password}
        onChange={handleChange}
        required
      />
    
      <Box 
        sx={{ 
          mt: 4, 
          mb: 2,
          width: '100%'
        }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          // fullWidth
          size="large"
          disabled={loading || !!emailError || !formData.email || !formData.password}
          sx={{ 
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            minHeight: '48px',
            textTransform: 'none',
            position: 'relative'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>Creating User...</span>
            </Box>
          ) : (
            'Create User'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateUserForm; 